/**
 * widgets/registry.toolbox.tsx — Widget systémových updatů (toolbox).
 *
 * Kompozitní (interaktivní) widget: obsluha přes MCP tooly toolbox modulu, node-aware
 * přes callWidgetTool (lokál → MCP, peer → mesh /call). Ukazuje CO se updatuje
 * (ACM commit + OS balíčky), JAK to pokračuje (živý log z sysupdate_progress, poll
 * á 2 s) a po dokončení vyzve k RESTARTU (reboot_required / services_need_restart)
 * s možností restartovat hned nebo naplánovat.
 */
import { useEffect, useRef, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import {
  Activity,
  AlertTriangle,
  Archive,
  BellOff,
  Calendar,
  Clock,
  Cpu,
  Download,
  Eye,
  GitCommit,
  HardDrive,
  Package,
  Pause,
  Puzzle,
  RefreshCw,
  RotateCw,
  Settings,
  Shield,
  Trash2,
} from 'lucide-react';
import { FleetRolloutCard } from '../components/FleetRolloutCard';
import {
  AcmLinkPanel,
  CleanerPanel,
  CrashRecoveryPanel,
  HealthPanel,
  ResourcesPanel,
  SecurityPanel,
  SystemPanel,
  ToolboxBackupPanel,
  WatchdogPanel,
} from '../pages/modules/toolbox/panels';
import { Alert } from '../components/ui/Alert';
import { Badge } from '../components/ui/Badge';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Modal } from '../components/ui/Modal';
import { Select } from '../components/ui/Select';
import { Toggle } from '../components/ui/Toggle';
import { useWidgetData } from './hooks';
import { useNodeDisplayLabel } from './NodePicker';
import { callWidgetTool } from './scope';
import type { WidgetRenderProps, WidgetSpec } from './types';

interface GitInfo {
  commit: string;
  message: string;
  branch: string;
  updates_available: boolean;
  updates_count: number;
}
interface HostRuntime {
  uptime_seconds: number | null;
  boot_time: string | null;
  boot_id: string | null;
}
interface StatusResp {
  git: GitInfo;
  services: Record<string, string>;
  host?: HostRuntime;
}

/** Fáze progressu po rebootu / restartu služeb (i18n). */
type ProgressPhase =
  | 'idle'
  | 'sending'
  | 'waiting_down'
  | 'waiting_up'
  | 'online'
  | 'timeout';

interface RebootProgressState {
  kind: 'reboot' | 'services';
  phase: ProgressPhase;
  startedAt: number;
  /** boot_id před rebootem — po návratu se musí lišit */
  prevBootId: string | null;
  /** uptime před rebootem */
  prevUptime: number | null;
  successPolls: number;
  nodeKey: string;
}

const REBOOT_SS_KEY = 'acm_sysupdate_reboot_progress';
const REBOOT_TIMEOUT_MS = 8 * 60_000; // 8 min
const ONLINE_POLLS_NEEDED = 2;

function loadRebootProgress(nodeKey: string): RebootProgressState | null {
  try {
    const raw = sessionStorage.getItem(REBOOT_SS_KEY);
    if (!raw) return null;
    const p = JSON.parse(raw) as RebootProgressState;
    if (p?.nodeKey !== nodeKey) return null;
    if (Date.now() - (p.startedAt ?? 0) > REBOOT_TIMEOUT_MS + 60_000) {
      sessionStorage.removeItem(REBOOT_SS_KEY);
      return null;
    }
    return p;
  } catch {
    return null;
  }
}

function saveRebootProgress(p: RebootProgressState | null) {
  try {
    if (!p || p.phase === 'idle' || p.phase === 'online') {
      sessionStorage.removeItem(REBOOT_SS_KEY);
    } else {
      sessionStorage.setItem(REBOOT_SS_KEY, JSON.stringify(p));
    }
  } catch { /* */ }
}

function fmtUptime(
  sec: number | null | undefined,
  t: (k: string, d: string, o?: Record<string, unknown>) => string,
): string {
  if (sec == null || !Number.isFinite(sec) || sec < 0) return '—';
  const d = Math.floor(sec / 86400);
  const h = Math.floor((sec % 86400) / 3600);
  const m = Math.floor((sec % 3600) / 60);
  if (d > 0) {
    return t('toolbox.sysupdate.uptimeDh', '{{d}}d {{h}}h', { d, h });
  }
  if (h > 0) {
    return t('toolbox.sysupdate.uptimeHm', '{{h}}h {{m}}m', { h, m });
  }
  return t('toolbox.sysupdate.uptimeM', '{{m}} min', { m: Math.max(1, m) });
}
interface OsPackage {
  name: string;
  current?: string;
  available?: string;
  category?: 'security' | 'kernel' | 'regular';
  /** Vzor z výjimek automatiky — balíček se aktualizuje jen ručně. */
  excluded_by?: string;
}
interface AptPkgName {
  name: string;
  version?: string;
}
interface OsResp {
  summary?: {
    security?: number;
    kernel?: number;
    regular?: number;
    total?: number;
    new?: number;
    autoremove?: number;
    excluded?: number;
  };
  reboot_required: boolean;
  reboot_reason?: string | null;
  kernel_update_available?: boolean;
  running_kernel?: string;
  newest_kernel?: string;
  services_need_restart: string[];
  packages: OsPackage[];
  /** NEW packages z apt upgrade --with-new-pkgs (nové linux-image, lib*) */
  new_packages?: AptPkgName[];
  /** apt autoremove kandidáti — „již nejsou potřeba“ */
  autoremove?: AptPkgName[];
}
interface SchedResp {
  scheduled: boolean;
  at?: string;
}
interface ChannelProg {
  running: boolean;
  started_at: string | null;
  log: string;
}
interface ProgResp {
  running: boolean;
  reboot_required: boolean;
  acm: ChannelProg;
  os: ChannelProg;
}
interface AptPreset {
  id: string;
  label: string;
  url: string;
}
interface AptSourceFile {
  path: string;
  name: string;
  enabled: boolean;
  kind: 'debian-main' | 'third-party' | 'acm-custom';
  lines: string[];
}
interface AptSourcesResp {
  ok?: boolean;
  suite?: string;
  debian_mirror?: { url: string; id: string } | null;
  presets?: AptPreset[];
  sources?: AptSourceFile[];
  keyring_issues?: string[];
  note?: string;
}
interface AutoQuiet {
  enabled: boolean;
  start: string;
  end: string;
  days: string;
}
interface AutoPolicy {
  enabled: boolean;
  os: boolean;
  os_security_only: boolean;
  os_exclude?: string[];
  acm: boolean;
  tz: string;
  install_time: string;
  install_days: string;
  install_window_min: number;
  quiet_hours: AutoQuiet;
  reboot: 'never' | 'outside_quiet' | 'at_install_time';
  defer_until: string | null;
  defer_reason: string | null;
}
interface AutoResp {
  ok?: boolean;
  policy?: AutoPolicy;
  decision?: { action: string; reason: string; detail?: string; next_local?: string };
  in_quiet_hours?: boolean;
  exclude_presets?: Record<string, string[]>;
}

/** Stejná pravidla jako sysupdate-exclude.ts — server je autorita, tohle jen hlídá vstup. */
const EXCLUDE_PATTERN_RE = /^[a-z0-9*?][a-z0-9+.*?_-]{0,99}$/;

export function SysUpdateWidget({ scope }: WidgetRenderProps) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const nodeLabel = useNodeDisplayLabel(
    scope.node,
    t('toolbox.sysupdate.local', 'lokál'),
  );
  const nodeKey = scope.node ?? 'local';
  const refetchAll = () =>
    qc.invalidateQueries({ predicate: (q) => q.queryKey[0] === 'widget-data' });

  const [progress, setProgress] = useState<RebootProgressState | null>(() =>
    loadRebootProgress(nodeKey)
  );
  const inProgress = !!progress &&
    progress.phase !== 'idle' &&
    progress.phase !== 'online';

  // Během rebootu agresivní poll (3 s), jinak klidnější intervaly.
  const status = useWidgetData<StatusResp>('sysupdate_status', {}, scope, {
    refetchInterval: inProgress ? 3_000 : 20_000,
  });
  const os = useWidgetData<OsResp>('sysupdate_os_status', {}, scope, {
    refetchInterval: inProgress ? 10_000 : 60_000,
  });
  const prog = useWidgetData<ProgResp>('sysupdate_progress', { tail: 400 }, scope, {
    refetchInterval: inProgress ? 3_000 : 2_000,
  });
  // Naplánovaný reboot (sysupdate_reboot bez argumentů = jen stav). Refetch po
  // naplánování/zrušení jde přes refetchAll (invaliduje i tenhle dotaz).
  const sched = useWidgetData<SchedResp>('sysupdate_reboot', {}, scope);
  const aptSrc = useWidgetData<AptSourcesResp>(
    'sysupdate_apt_sources',
    { action: 'list' },
    scope,
    { refetchInterval: 120_000 },
  );
  const auto = useWidgetData<AutoResp>(
    'sysupdate_autoupdate',
    { action: 'get' },
    scope,
    { refetchInterval: 30_000 },
  );

  const [mirrorId, setMirrorId] = useState('');
  const [customMirrorUrl, setCustomMirrorUrl] = useState('');
  const [acmRepoLine, setAcmRepoLine] = useState('');
  const [showSources, setShowSources] = useState(false);
  const [elapsedTick, setElapsedTick] = useState(0);
  const [autoTime, setAutoTime] = useState('03:00');
  const [autoDays, setAutoDays] = useState('daily');
  const [quietOn, setQuietOn] = useState(false);
  const [quietStart, setQuietStart] = useState('08:00');
  const [quietEnd, setQuietEnd] = useState('22:00');
  const [autoOs, setAutoOs] = useState(false);
  const [autoOsSec, setAutoOsSec] = useState(true);
  const [autoAcm, setAutoAcm] = useState(true);
  const [autoReboot, setAutoReboot] = useState<'never' | 'outside_quiet' | 'at_install_time'>(
    'outside_quiet',
  );
  const [autoExclude, setAutoExclude] = useState<string[]>([]);
  const [excludeDraft, setExcludeDraft] = useState('');
  const [showAutoDetails, setShowAutoDetails] = useState(false);
  const autoSeeded = useRef(false);

  const running = prog.data?.running ?? false;
  const acmRun = prog.data?.acm?.running ?? false;
  const osRun = prog.data?.os?.running ?? false;
  const rebootReq = (os.data?.reboot_required ?? false) || (prog.data?.reboot_required ?? false);
  const rebootReason = os.data?.reboot_reason ?? null;
  const kernelAvail = os.data?.kernel_update_available ?? false;
  const svcRestart = os.data?.services_need_restart ?? [];
  const needsRestart = rebootReq || svcRestart.length > 0;
  const schedInfo = sched.data;
  const host = status.data?.host;
  const services = status.data?.services ?? {};

  // Živý log — přednost běžícímu kanálu, jinak poslední neprázdný.
  const activeLog = acmRun
    ? prog.data?.acm?.log
    : osRun
    ? prog.data?.os?.log
    : (prog.data?.acm?.log || prog.data?.os?.log || '');

  const beginProgress = (
    kind: 'reboot' | 'services',
    phase: ProgressPhase = 'sending',
  ) => {
    const next: RebootProgressState = {
      kind,
      phase,
      startedAt: Date.now(),
      prevBootId: host?.boot_id ?? null,
      prevUptime: host?.uptime_seconds ?? null,
      successPolls: 0,
      nodeKey,
    };
    setProgress(next);
    saveRebootProgress(next);
  };

  const patchProgress = (patch: Partial<RebootProgressState>) => {
    setProgress((prev) => {
      if (!prev) return prev;
      const next = { ...prev, ...patch };
      saveRebootProgress(next);
      return next;
    });
  };

  const clearProgress = (opts?: { toastBack?: boolean }) => {
    setProgress(null);
    saveRebootProgress(null);
    if (opts?.toastBack) {
      toast.success(t('toolbox.sysupdate.toastNodeBack', { node: nodeLabel }));
    }
    refetchAll();
  };

  // Tik pro elapsed čas v progress panelu
  useEffect(() => {
    if (!inProgress) return;
    const id = setInterval(() => setElapsedTick((n) => n + 1), 1000);
    return () => clearInterval(id);
  }, [inProgress]);

  // Obnova progressu po přepnutí nodu
  useEffect(() => {
    setProgress(loadRebootProgress(nodeKey));
  }, [nodeKey]);

  // State machine: pooluje status během rebootu / restartu služeb
  useEffect(() => {
    if (!progress || progress.phase === 'idle' || progress.phase === 'online') return;

    const elapsed = Date.now() - progress.startedAt;
    if (elapsed > REBOOT_TIMEOUT_MS) {
      patchProgress({ phase: 'timeout' });
      return;
    }

    const unreachable = status.isError || prog.isError || status.fetchStatus === 'paused';
    // isFetching bez dat po chybě = stále dolů
    const hasData = status.isSuccess && !!status.data;
    const bootId = status.data?.host?.boot_id ?? null;
    const uptime = status.data?.host?.uptime_seconds ?? null;
    const svcVals = Object.values(status.data?.services ?? {});
    const activeN = svcVals.filter((s) => s === 'active').length;
    const servicesOk = svcVals.length === 0
      ? hasData
      : activeN >= Math.max(1, Math.ceil(svcVals.length * 0.5));

    if (progress.kind === 'reboot') {
      if (progress.phase === 'sending' || progress.phase === 'waiting_down') {
        // Po odeslání: buď network fail, nebo boot_id se ještě nezměnil a uptime roste → waiting_down
        if (unreachable || status.isFetching && !hasData) {
          if (progress.phase !== 'waiting_down') patchProgress({ phase: 'waiting_down' });
          return;
        }
        // Stroj ještě odpovídá starým boot_id — po chvíli ber jako waiting_down (shutdown probíhá)
        if (elapsed > 8_000 && progress.phase === 'sending') {
          patchProgress({ phase: 'waiting_down' });
        }
        // Detekce že už je dole a zase nahoře s novým bootem
        if (hasData && progress.prevBootId && bootId && bootId !== progress.prevBootId) {
          patchProgress({ phase: 'waiting_up', successPolls: 1 });
          return;
        }
        if (
          hasData &&
          progress.prevUptime != null &&
          uptime != null &&
          uptime + 30 < progress.prevUptime
        ) {
          // uptime skočil dolů = reboot
          patchProgress({ phase: 'waiting_up', successPolls: 1 });
          return;
        }
        return;
      }

      if (progress.phase === 'waiting_up' || progress.phase === 'waiting_down') {
        if (unreachable) {
          if (progress.phase !== 'waiting_down') {
            patchProgress({ phase: 'waiting_down', successPolls: 0 });
          }
          return;
        }
        if (!hasData) return;

        const rebooted = (progress.prevBootId && bootId && bootId !== progress.prevBootId) ||
          (progress.prevUptime != null && uptime != null && uptime + 30 < progress.prevUptime) ||
          // bez boot_id: po výpadku stačí stabilní success
          (!progress.prevBootId && progress.phase === 'waiting_up');

        if (progress.phase === 'waiting_down' && !unreachable && rebooted) {
          patchProgress({ phase: 'waiting_up', successPolls: 1 });
          return;
        }

        if (progress.phase === 'waiting_down' && !unreachable && elapsed > 45_000 && servicesOk) {
          // výpadek jsme nechytili (rychlý reboot / cache) — přejdi na waiting_up
          patchProgress({ phase: 'waiting_up', successPolls: 1 });
          return;
        }

        if (progress.phase === 'waiting_up' && servicesOk) {
          const n = (progress.successPolls ?? 0) + 1;
          if (n >= ONLINE_POLLS_NEEDED) {
            patchProgress({ phase: 'online', successPolls: n });
            // krátká „online“ animace, pak clear
            setTimeout(() => clearProgress({ toastBack: true }), 1600);
          } else {
            patchProgress({ successPolls: n });
          }
        }
      }
    }

    if (progress.kind === 'services') {
      if (progress.phase === 'sending') {
        patchProgress({ phase: 'waiting_up' });
        return;
      }
      if (progress.phase === 'waiting_up') {
        if (!hasData) return;
        // po restartu služeb: většina active a min. 5 s od startu
        if (servicesOk && elapsed > 5_000) {
          const n = (progress.successPolls ?? 0) + 1;
          if (n >= ONLINE_POLLS_NEEDED) {
            patchProgress({ phase: 'online', successPolls: n });
            setTimeout(() => clearProgress({ toastBack: true }), 1200);
          } else {
            patchProgress({ successPolls: n });
          }
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    progress?.phase,
    progress?.kind,
    progress?.startedAt,
    progress?.successPolls,
    status.isError,
    status.isSuccess,
    status.isFetching,
    status.dataUpdatedAt,
    prog.isError,
    status.data?.host?.boot_id,
    status.data?.host?.uptime_seconds,
    JSON.stringify(status.data?.services),
  ]);

  // Node-aware akce přes MCP tooly toolboxu (rules-of-hooks: každý useMutation
  // deklarovaný napevno na top-levelu, stabilní pořadí).
  const updAcm = useMutation({
    mutationFn: () =>
      callWidgetTool('sysupdate_update', { build: true, restart: true, async: true }, scope),
    onSuccess: refetchAll,
  });
  // OS upgrade BEZ auto-autoremove — úklid jen po potvrzení v dialogu
  const updOs = useMutation({
    mutationFn: () => callWidgetTool('sysupdate_os_update', { autoremove: false }, scope),
    onSuccess: () => {
      refetchAll();
      toast.info(t('toolbox.sysupdate.toastOsDone'));
    },
  });
  const updOsSec = useMutation({
    mutationFn: () => callWidgetTool('sysupdate_os_update', { security_only: true }, scope),
    onSuccess: refetchAll,
  });
  const osAutoremove = useMutation({
    mutationFn: () =>
      callWidgetTool(
        'sysupdate_os_update',
        { only_autoremove: true, confirm: true },
        scope,
      ),
    onSuccess: (r) => {
      // MCP vrací requires_confirm i při „chybném“ ok:false — ošetři
      const res = r as { ok?: boolean; requires_confirm?: boolean; error?: string };
      if (res?.requires_confirm) {
        toast.error(res.error || t('toolbox.sysupdate.toastMissingConfirm'));
        return;
      }
      if (res && res.ok === false) {
        toast.error(res.error || t('toolbox.sysupdate.toastAutoremoveFailed'));
        return;
      }
      toast.success(t('toolbox.sysupdate.toastAutoremoveDone'));
      setArModal(false);
      refetchAll();
    },
    onError: (e) =>
      toast.error((e as Error).message || t('toolbox.sysupdate.toastAutoremoveFailed')),
  });
  const restartSvc = useMutation({
    mutationFn: () => callWidgetTool('sysupdate_restart', { service: 'all' }, scope),
    onMutate: () => {
      beginProgress('services', 'sending');
    },
    onSuccess: () => {
      patchProgress({ phase: 'waiting_up' });
      refetchAll();
    },
    onError: (e) => {
      clearProgress();
      toast.error((e as Error).message || t('toolbox.sysupdate.toastRestartFailed'));
    },
  });
  const [rebootModal, setRebootModal] = useState(false);
  const [arModal, setArModal] = useState(false);
  const [installModal, setInstallModal] = useState<'security' | 'all' | null>(null);
  const [schedWhen, setSchedWhen] = useState('');

  // Reboot stroje — progress panel + poll (ne okamžitý návrat k tlačítku).
  const armWaitingDown = () => {
    setTimeout(() => {
      setProgress((p) => {
        if (!p || p.kind !== 'reboot' || p.phase !== 'sending') return p;
        const next = { ...p, phase: 'waiting_down' as const };
        saveRebootProgress(next);
        return next;
      });
    }, 2500);
  };
  const rebootNow = useMutation({
    mutationFn: () => callWidgetTool('sysupdate_reboot', { when: 'now', confirm: true }, scope),
    onMutate: () => {
      beginProgress('reboot', 'sending');
      setRebootModal(false);
      armWaitingDown();
      toast.info(t('toolbox.sysupdate.toastRebootStarted', { node: nodeLabel }));
    },
    onSuccess: () => {
      // odpověď stihla přijít před shutdownem — pořád čekáme na down/up
      patchProgress({ phase: 'waiting_down' });
    },
    onError: (e) => {
      const msg = (e as Error).message || '';
      // Okamžitý reboot přeruší spojení → network/unreachable = OČEKÁVANÉ, ne chyba.
      if (/network|fetch|unreachable|load failed|failed to fetch|ECONNREFUSED|timeout/i.test(msg)) {
        patchProgress({ phase: 'waiting_down' });
      } else {
        clearProgress();
        toast.error(msg || t('toolbox.sysupdate.toastRebootFailed'));
      }
    },
  });
  const rebootSched = useMutation({
    mutationFn: (when: string) => callWidgetTool('sysupdate_reboot', { when }, scope),
    onSuccess: () => {
      toast.success(t('toolbox.sysupdate.toastRebootScheduled'));
      setSchedWhen('');
      refetchAll();
    },
    onError: (e) => toast.error((e as Error).message || t('toolbox.sysupdate.toastScheduleFailed')),
  });
  const rebootCancel = useMutation({
    mutationFn: () => callWidgetTool('sysupdate_reboot', { cancel: true }, scope),
    onSuccess: () => {
      toast.success(t('toolbox.sysupdate.toastScheduleCancelled'));
      refetchAll();
    },
    onError: (e) => toast.error((e as Error).message || t('toolbox.sysupdate.toastCancelFailed')),
  });

  void elapsedTick; // force re-render for elapsed clock

  // Po doběhnutí updatu (running: true → false) reálně přeověř stav — fresh os_status
  // (apt update + kernel/needrestart). Kernel se objeví jako „reboot required" AŽ po
  // instalaci (běžící ≠ nejnovější nainstalovaný), takže bez re-checku by widget
  // ukazoval starý stav.
  const prevRunning = useRef(false);
  useEffect(() => {
    if (prevRunning.current && !running) refetchAll();
    prevRunning.current = running;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [running]);

  const setMirror = useMutation({
    mutationFn: () => {
      if (mirrorId === 'custom' || (!mirrorId && customMirrorUrl.trim())) {
        return callWidgetTool(
          'sysupdate_apt_sources',
          { action: 'set_debian_mirror', mirror_id: 'custom', mirror_url: customMirrorUrl.trim() },
          scope,
        );
      }
      return callWidgetTool(
        'sysupdate_apt_sources',
        { action: 'set_debian_mirror', mirror_id: mirrorId || aptSrc.data?.debian_mirror?.id },
        scope,
      );
    },
    onSuccess: (r) => {
      const ok = (r as { ok?: boolean })?.ok !== false;
      if (ok) toast.success(t('toolbox.sysupdate.toastMirrorSet'));
      else {
        toast.error(
          String((r as { error?: string })?.error ?? t('toolbox.sysupdate.toastMirrorFailed')),
        );
      }
      refetchAll();
      aptSrc.refetch();
    },
    onError: (e) => toast.error((e as Error).message),
  });
  const toggleSource = useMutation({
    mutationFn: (p: { source_name: string; enabled: boolean }) =>
      callWidgetTool(
        'sysupdate_apt_sources',
        { action: 'set_enabled', source_name: p.source_name, enabled: p.enabled },
        scope,
      ),
    onSuccess: () => {
      toast.success(t('toolbox.sysupdate.toastSourceUpdated'));
      aptSrc.refetch();
      refetchAll();
    },
    onError: (e) => toast.error((e as Error).message),
  });
  const setAcmRepo = useMutation({
    mutationFn: () =>
      callWidgetTool(
        'sysupdate_apt_sources',
        { action: 'set_custom_acm', acm_line: acmRepoLine.trim() },
        scope,
      ),
    onSuccess: (r) => {
      if ((r as { ok?: boolean })?.ok === false) {
        toast.error(
          String((r as { error?: string })?.error ?? t('toolbox.sysupdate.toastAcmRepoFailed')),
        );
      } else {
        toast.success(t('toolbox.sysupdate.toastAcmRepoSaved'));
        setAcmRepoLine('');
      }
      aptSrc.refetch();
    },
    onError: (e) => toast.error((e as Error).message),
  });
  const removeAcmRepo = useMutation({
    mutationFn: () =>
      callWidgetTool('sysupdate_apt_sources', { action: 'remove_custom_acm' }, scope),
    onSuccess: () => {
      toast.success(t('toolbox.sysupdate.toastAcmRepoRemoved'));
      aptSrc.refetch();
    },
    onError: (e) => toast.error((e as Error).message),
  });

  const saveAuto = useMutation({
    mutationFn: async (patch: Record<string, unknown>) => {
      const r = await callWidgetTool(
        'sysupdate_autoupdate',
        { action: 'set', ...patch },
        scope,
      ) as { ok?: boolean; error?: string };
      if (r && r.ok === false) {
        throw new Error(r.error || t('toolbox.sysupdate.autoToastFailed'));
      }
      return r;
    },
    onSuccess: () => {
      auto.refetch();
    },
    onError: (e) =>
      toast.error((e as Error).message || t('toolbox.sysupdate.autoToastFailed')),
  });
  const deferAuto = useMutation({
    mutationFn: (preset: string) =>
      callWidgetTool('sysupdate_autoupdate', { action: 'defer', preset }, scope),
    onSuccess: () => {
      toast.success(t('toolbox.sysupdate.autoToastDeferred'));
      auto.refetch();
    },
    onError: (e) =>
      toast.error((e as Error).message || t('toolbox.sysupdate.autoToastFailed')),
  });
  const clearDeferAuto = useMutation({
    mutationFn: () =>
      callWidgetTool('sysupdate_autoupdate', { action: 'clear_defer' }, scope),
    onSuccess: () => {
      toast.success(t('toolbox.sysupdate.autoToastDeferCleared'));
      auto.refetch();
    },
    onError: (e) =>
      toast.error((e as Error).message || t('toolbox.sysupdate.autoToastFailed')),
  });

  const persistAuto = (patch: Record<string, unknown>) =>
    saveAuto.mutateAsync({
      install_time: autoTime,
      install_days: autoDays,
      os: autoOs,
      os_security_only: autoOsSec,
      os_exclude: autoExclude,
      acm: autoAcm,
      reboot: autoReboot,
      quiet_hours: {
        enabled: quietOn,
        start: quietStart,
        end: quietEnd,
        days: 'daily',
      },
      ...patch,
    });
  const policy = auto.data?.policy;
  const acmAutoOn = !!(policy?.enabled && policy?.acm);
  const osAutoOn = !!(policy?.enabled && policy?.os);
  const autoOn = acmAutoOn || osAutoOn;
  const autoStatusText = (() => {
    const until = auto.data?.decision?.next_local
      || (policy?.defer_until ? new Date(policy.defer_until).toLocaleString() : '');
    const quietUntil = policy?.quiet_hours?.end || quietEnd;
    if (policy?.defer_until) {
      return t('toolbox.sysupdate.autoStatusDeferred', { until: until || '—' });
    }
    if (auto.data?.in_quiet_hours) {
      return t('toolbox.sysupdate.autoStatusQuiet', { until: quietUntil || '—' });
    }
    if (!autoOn) return t('toolbox.sysupdate.autoStatusDisabled');
    const reason = auto.data?.decision?.reason;
    if (reason === 'waiting_time' || reason === 'wrong_day') {
      return t('toolbox.sysupdate.autoStatusWaiting', { until: until || autoTime });
    }
    if (reason === 'frozen') return t('toolbox.sysupdate.autoStatusFrozen');
    if (reason === 'running') return t('toolbox.sysupdate.autoStatusRunning');
    if (reason === 'maintenance') return t('toolbox.sysupdate.autoStatusMaintenance');
    if (reason === 'eligible') return t('toolbox.sysupdate.autoStatusReady');
    return t('toolbox.sysupdate.autoStatusIdle');
  })();

  const busy = updAcm.isPending || updOs.isPending || updOsSec.isPending ||
    osAutoremove.isPending || running || inProgress ||
    setMirror.isPending || toggleSource.isPending || setAcmRepo.isPending ||
    saveAuto.isPending || deferAuto.isPending || clearDeferAuto.isPending;
  const s = os.data?.summary ?? {};
  /** Co instaluje sysupdate_os_update { security_only }: bezpečnostní opravy + jádro. */
  const secPending = (s.security ?? 0) + (s.kernel ?? 0);
  const gi = status.data?.git;
  const newPkgs = os.data?.new_packages ?? [];
  const autoPkgs = os.data?.autoremove ?? [];

  const shortCommit = gi?.commit ? gi.commit.slice(0, 8) : '—';
  const presets = aptSrc.data?.presets ?? [];
  const curMirror = aptSrc.data?.debian_mirror;
  const thirdParty = (aptSrc.data?.sources ?? []).filter((x) => x.kind !== 'debian-main');
  const hasAcmCustom = thirdParty.some((x) => x.kind === 'acm-custom' && x.enabled);

  // Sync select default once data loads
  useEffect(() => {
    if (!mirrorId && curMirror?.id) {
      setMirrorId(curMirror.id === 'custom' ? 'custom' : curMirror.id);
    }
    if (!customMirrorUrl && curMirror?.id === 'custom' && curMirror.url) {
      setCustomMirrorUrl(curMirror.url);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [curMirror?.id, curMirror?.url]);

  useEffect(() => {
    autoSeeded.current = false;
  }, [nodeKey]);
  useEffect(() => {
    const p = auto.data?.policy;
    if (!p || autoSeeded.current) return;
    autoSeeded.current = true;
    setAutoTime(p.install_time || '03:00');
    setAutoDays(p.install_days || 'daily');
    setQuietOn(p.quiet_hours?.enabled === true);
    setQuietStart(p.quiet_hours?.start || '08:00');
    setQuietEnd(p.quiet_hours?.end || '22:00');
    setAutoOs(p.os === true);
    setAutoOsSec(p.os_security_only !== false);
    setAutoAcm(p.acm !== false);
    setAutoReboot(p.reboot || 'outside_quiet');
    setAutoExclude(p.os_exclude ?? []);
  }, [auto.data?.policy]);

  const saveExclude = async (next: string[]) => {
    const prev = autoExclude;
    setAutoExclude(next);
    try {
      await persistAuto({ os_exclude: next });
      os.refetch();
    } catch {
      setAutoExclude(prev);
    }
  };
  const addExclude = (raw: string) => {
    const items = raw.split(/[\s,]+/).map((x) => x.trim().toLowerCase()).filter(Boolean);
    const bad = items.filter((x) => !EXCLUDE_PATTERN_RE.test(x));
    if (bad.length > 0) {
      toast.error(t('toolbox.sysupdate.excludeInvalid', { items: bad.join(', ') }));
      return;
    }
    const next = [...autoExclude, ...items.filter((x) => !autoExclude.includes(x))];
    if (next.length !== autoExclude.length) void saveExclude(next);
    setExcludeDraft('');
  };
  const excludedPkgs = (os.data?.packages ?? []).filter((p) => p.excluded_by);
  const dbPreset = auto.data?.exclude_presets?.databases ?? [];

  return (
    <div className='h-full flex flex-col gap-3 p-3 text-sm overflow-auto'>
      {/* Hlavička: cíl + git commit + uptime */}
      <div className='flex items-center justify-between gap-2 flex-wrap'>
        <div className='flex items-center gap-2 min-w-0 flex-wrap'>
          <GitCommit size={14} className='text-gray-400 shrink-0' />
          <span className='font-mono text-xs text-gray-300 truncate' title={gi?.message}>
            {shortCommit}
          </span>
          <span className='text-xs text-gray-500'>· {nodeLabel}</span>
          {host?.uptime_seconds != null && (
            <span
              className='text-[11px] text-gray-500 tabular-nums'
              title={host.boot_time
                ? t('toolbox.sysupdate.bootTimeTitle', 'Boot: {{t}}', {
                  t: new Date(host.boot_time).toLocaleString(),
                })
                : undefined}
            >
              · {t('toolbox.sysupdate.uptimeLabel', 'běží')}{' '}
              <span className='text-gray-300'>
                {fmtUptime(host.uptime_seconds, t as never)}
              </span>
              {host.boot_time && (
                <span className='text-gray-600'>
                  {' '}({t('toolbox.sysupdate.sinceBoot', 'od')}{' '}
                  {new Date(host.boot_time).toLocaleString(undefined, {
                    day: '2-digit',
                    month: '2-digit',
                    hour: '2-digit',
                    minute: '2-digit',
                  })})
                </span>
              )}
            </span>
          )}
        </div>
        <div className='flex items-center gap-2 shrink-0'>
          {gi?.updates_available
            ? (
              <Badge variant='warning'>
                {t('toolbox.sysupdate.acmUpdates', { count: gi.updates_count })}
              </Badge>
            )
            : <Badge variant='success'>{t('toolbox.sysupdate.acmUpToDate')}</Badge>}
          <Button
            size='sm'
            variant='ghost'
            disabled={busy || inProgress}
            loading={status.isFetching || os.isFetching}
            onClick={() => {
              status.refetch();
              os.refetch();
              prog.refetch();
            }}
          >
            <RefreshCw size={13} /> {t('toolbox.sysupdate.check')}
          </Button>
        </div>
      </div>

      {/* ACM — ruční update + automaticky (čas / nerušit / odložení) */}
      <div className='flex flex-col gap-2 border-t border-gray-700/50 pt-2'>
        <div className='flex items-center justify-between gap-2 flex-wrap'>
          <div className='flex items-center gap-2 min-w-0'>
            <span className='text-gray-400'>{t('toolbox.sysupdate.acmCore')}</span>
            {acmAutoOn && <Badge variant='success'>{t('toolbox.sysupdate.autoOn')}</Badge>}
            {auto.data?.in_quiet_hours && (
              <Badge variant='warning'>{t('toolbox.sysupdate.autoQuiet')}</Badge>
            )}
            {policy?.defer_until && (
              <Badge variant='warning'>{t('toolbox.sysupdate.autoDefer')}</Badge>
            )}
          </div>
          <div className='flex items-center gap-3 shrink-0'>
            <Toggle
              value={acmAutoOn}
              saving={saveAuto.isPending}
              disabled={busy && !saveAuto.isPending}
              label={t('toolbox.sysupdate.autoToggle')}
              onSave={async (v) => {
                setAutoAcm(v);
                await persistAuto({ enabled: v || autoOs, acm: v, os: autoOs });
              }}
            />
            <Button
              variant='primary'
              size='sm'
              loading={updAcm.isPending || acmRun}
              disabled={busy}
              onClick={() => updAcm.mutate()}
            >
              <Download size={13} /> {t('toolbox.sysupdate.updateAcm')}
            </Button>
          </div>
        </div>
      </div>

      {/* OS sekce */}
      <div className='flex flex-col gap-2 border-t border-gray-700/50 pt-2'>
        <div className='flex items-center justify-between gap-2 flex-wrap'>
          <div className='flex items-center gap-2 text-gray-400 flex-wrap'>
            <Package size={14} className='shrink-0' />
            <span>
              {t('toolbox.sysupdate.osSummary', { total: s.total ?? 0 })}
              {s.security ? t('toolbox.sysupdate.securityCount', { count: s.security }) : ''}
              {s.kernel ? t('toolbox.sysupdate.kernelCount', { count: s.kernel }) : ''}
              {(s.new ?? newPkgs.length) > 0
                ? t('toolbox.sysupdate.newCount', { count: s.new ?? newPkgs.length })
                : ''}
              {(s.autoremove ?? autoPkgs.length) > 0
                ? t('toolbox.sysupdate.autoremoveCount', {
                  count: s.autoremove ?? autoPkgs.length,
                })
                : ''}
            </span>
            {rebootReq && <Badge variant='error' dot>{t('toolbox.sysupdate.badgeReboot')}</Badge>}
            {!rebootReq && kernelAvail && (
              <Badge variant='warning'>{t('toolbox.sysupdate.kernelAvailable')}</Badge>
            )}
          </div>
          <div className='flex gap-2 shrink-0 flex-wrap justify-end items-center'>
            {autoPkgs.length > 0 && (
              <Button
                size='sm'
                loading={osAutoremove.isPending}
                disabled={busy}
                onClick={() => setArModal(true)}
                title={t('toolbox.sysupdate.cleanupTitle')}
              >
                {t('toolbox.sysupdate.cleanupBtn', { count: autoPkgs.length })}
              </Button>
            )}
            <Button
              size='sm'
              variant='ghost'
              loading={os.isFetching}
              disabled={busy}
              onClick={() => void os.refetch()}
              title={os.dataUpdatedAt
                ? t('toolbox.sysupdate.checkUpdatesLast', {
                  at: new Date(os.dataUpdatedAt).toLocaleTimeString(),
                })
                : undefined}
            >
              <RefreshCw size={13} /> {t('toolbox.sysupdate.checkUpdates')}
            </Button>
            <Button
              size='sm'
              loading={updOsSec.isPending}
              disabled={busy || secPending === 0}
              onClick={() => setInstallModal('security')}
            >
              <Shield size={13} /> {t('toolbox.sysupdate.installSecurityNow', { count: secPending })}
            </Button>
            <Button
              variant='primary'
              size='sm'
              loading={updOs.isPending || osRun}
              disabled={busy || (s.total ?? 0) === 0}
              onClick={() => setInstallModal('all')}
            >
              <Download size={13} /> {t('toolbox.sysupdate.installAllNow', { count: s.total ?? 0 })}
            </Button>
          </div>
        </div>
        <div className='flex flex-wrap items-center gap-3'>
          <span className='text-xs text-gray-400'>{t('toolbox.sysupdate.osAutoLabel')}</span>
          <Select
            size='sm'
            className='w-auto min-w-[11rem]'
            value={!osAutoOn ? 'off' : autoOsSec ? 'security' : 'all'}
            disabled={(busy && !saveAuto.isPending) || saveAuto.isPending}
            onChange={(e) => {
              const v = e.target.value as 'off' | 'security' | 'all';
              const on = v !== 'off';
              setAutoOs(on);
              if (on) setAutoOsSec(v === 'security');
              void persistAuto({
                enabled: on || autoAcm,
                os: on,
                acm: autoAcm,
                ...(on ? { os_security_only: v === 'security' } : {}),
              });
            }}
          >
            <option value='off'>{t('toolbox.sysupdate.osAutoOff')}</option>
            <option value='security'>{t('toolbox.sysupdate.osAutoSecurity')}</option>
            <option value='all'>{t('toolbox.sysupdate.osAutoAll')}</option>
          </Select>
          {!osAutoOn && (
            <span className='text-[11px] text-gray-500'>{t('toolbox.sysupdate.osAutoOffHint')}</span>
          )}
        </div>
        {osAutoOn && (
          <div className='flex flex-wrap items-center gap-3'>
            <span className='text-[11px] text-gray-500'>{t('toolbox.sysupdate.osAutoHint')}</span>
            <span className='text-xs text-gray-500'>{t('toolbox.sysupdate.autoReboot')}</span>
            <Select
              size='sm'
              className='w-auto min-w-[10rem]'
              value={autoReboot}
              onChange={(e) => {
                const v = e.target.value as 'never' | 'outside_quiet' | 'at_install_time';
                setAutoReboot(v);
                void persistAuto({ reboot: v, enabled: true, os: true });
              }}
            >
              <option value='outside_quiet'>{t('toolbox.sysupdate.autoRebootOutside')}</option>
              <option value='never'>{t('toolbox.sysupdate.autoRebootNever')}</option>
              <option value='at_install_time'>{t('toolbox.sysupdate.autoRebootAtTime')}</option>
            </Select>
            <Button
              size='sm'
              variant='ghost'
              onClick={() => setShowAutoDetails((v) => !v)}
              aria-expanded={showAutoDetails}
            >
              <Settings size={13} /> {t('toolbox.sysupdate.autoDetails')}
              {autoExclude.length > 0 && (
                <Badge variant='info'>
                  {t('toolbox.sysupdate.excludeCount', { count: autoExclude.length })}
                </Badge>
              )}
            </Button>
          </div>
        )}
        {osAutoOn && showAutoDetails && (
          <div className='flex flex-col gap-2 rounded-xl border border-gray-700/40 p-2'>
            <div className='text-xs text-gray-400'>{t('toolbox.sysupdate.excludeTitle')}</div>
            <p className='text-[11px] text-gray-500 leading-relaxed'>
              {t('toolbox.sysupdate.excludeHelp')}
            </p>
            {autoExclude.length === 0
              ? <span className='text-xs text-gray-500'>{t('toolbox.sysupdate.excludeNone')}</span>
              : (
                <div className='flex flex-wrap gap-1.5'>
                  {autoExclude.map((pat) => (
                    <span key={pat} className='inline-flex items-center gap-1'>
                      <Badge>
                        <span className='font-mono'>{pat}</span>
                      </Badge>
                      <Button
                        size='icon'
                        variant='ghost'
                        disabled={saveAuto.isPending}
                        title={t('toolbox.sysupdate.excludeRemove', { pattern: pat })}
                        aria-label={t('toolbox.sysupdate.excludeRemove', { pattern: pat })}
                        onClick={() => void saveExclude(autoExclude.filter((x) => x !== pat))}
                      >
                        <Trash2 size={12} />
                      </Button>
                    </span>
                  ))}
                </div>
              )}
            <div className='flex flex-wrap items-center gap-2'>
              <Input
                size='sm'
                className='w-full sm:w-56 font-mono'
                placeholder='mariadb-*'
                value={excludeDraft}
                onChange={(e) => setExcludeDraft(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && excludeDraft.trim()) addExclude(excludeDraft);
                }}
              />
              <Button
                size='sm'
                disabled={!excludeDraft.trim() || saveAuto.isPending}
                onClick={() => addExclude(excludeDraft)}
              >
                {t('toolbox.sysupdate.excludeAdd')}
              </Button>
              {dbPreset.some((x) => !autoExclude.includes(x)) && (
                <Button
                  size='sm'
                  variant='ghost'
                  disabled={saveAuto.isPending}
                  title={dbPreset.join(', ')}
                  onClick={() => addExclude(dbPreset.join(' '))}
                >
                  {t('toolbox.sysupdate.excludePresetDb')}
                </Button>
              )}
            </div>
            {excludedPkgs.length > 0 && (
              <Alert variant='warning'>
                {t('toolbox.sysupdate.excludePending', {
                  count: excludedPkgs.length,
                  names: excludedPkgs.map((p) => p.name).join(', '),
                })}
              </Alert>
            )}
          </div>
        )}
      </div>

      <div className='border-t border-gray-700/50 pt-2 flex flex-col gap-2'>
        <div className='flex items-center gap-2 text-xs text-gray-400'>
          <Clock size={14} className='shrink-0' />
          {t('toolbox.sysupdate.autoTitle')}
        </div>
        <p className='text-[11px] text-gray-500'>{t('toolbox.sysupdate.autoHint')}</p>
        <div className='flex flex-wrap items-center gap-2'>
          <span className='text-xs text-gray-500'>{t('toolbox.sysupdate.autoInstallAt')}</span>
          <Input
            type='time'
            size='sm'
            className='w-[7.5rem]'
            value={autoTime}
            onChange={(e) => setAutoTime(e.target.value)}
            onBlur={(e) => {
              void persistAuto({ install_time: e.target.value });
            }}
          />
          <Select
            size='sm'
            className='w-auto min-w-[8rem]'
            value={autoDays}
            onChange={(e) => {
              const v = e.target.value;
              setAutoDays(v);
              void persistAuto({ install_days: v });
            }}
          >
            <option value='daily'>{t('toolbox.sysupdate.autoDaily')}</option>
            <option value='mon-fri'>{t('toolbox.sysupdate.autoWeekdays')}</option>
            <option value='sat-sun'>{t('toolbox.sysupdate.autoWeekend')}</option>
          </Select>
        </div>
        <div className='flex flex-wrap items-center gap-2'>
          <BellOff size={13} className='text-gray-500 shrink-0' />
          <Toggle
            value={quietOn}
            saving={saveAuto.isPending}
            disabled={busy && !saveAuto.isPending}
            label={t('toolbox.sysupdate.autoQuiet')}
            onSave={async (v) => {
              setQuietOn(v);
              await persistAuto({
                quiet_hours: {
                  enabled: v,
                  start: quietStart,
                  end: quietEnd,
                  days: 'daily',
                },
              });
            }}
          />
          <span className='text-xs text-gray-500'>{t('toolbox.sysupdate.autoQuietFrom')}</span>
          <Input
            type='time'
            size='sm'
            className='w-[7.5rem]'
            value={quietStart}
            disabled={!quietOn}
            onChange={(e) => setQuietStart(e.target.value)}
            onBlur={(e) => {
              void persistAuto({
                quiet_hours: {
                  enabled: quietOn,
                  start: e.target.value,
                  end: quietEnd,
                  days: 'daily',
                },
              });
            }}
          />
          <span className='text-xs text-gray-500'>{t('toolbox.sysupdate.autoQuietTo')}</span>
          <Input
            type='time'
            size='sm'
            className='w-[7.5rem]'
            value={quietEnd}
            disabled={!quietOn}
            onChange={(e) => setQuietEnd(e.target.value)}
            onBlur={(e) => {
              void persistAuto({
                quiet_hours: {
                  enabled: quietOn,
                  start: quietStart,
                  end: e.target.value,
                  days: 'daily',
                },
              });
            }}
          />
        </div>
        <div className='flex flex-wrap items-center gap-2'>
          <Pause size={13} className='text-gray-500 shrink-0' />
          <span className='text-xs text-gray-500'>{t('toolbox.sysupdate.autoDefer')}</span>
          {(['1h', '1d', '7d'] as const).map((p) => (
            <Button
              key={p}
              size='sm'
              variant='ghost'
              disabled={busy}
              loading={deferAuto.isPending && deferAuto.variables === p}
              onClick={() => deferAuto.mutate(p)}
            >
              {p === '1h'
                ? t('toolbox.sysupdate.autoDefer1h')
                : p === '1d'
                ? t('toolbox.sysupdate.autoDefer1d')
                : t('toolbox.sysupdate.autoDefer7d')}
            </Button>
          ))}
          {policy?.defer_until && (
            <Button
              size='sm'
              variant='ghost'
              disabled={busy}
              loading={clearDeferAuto.isPending}
              onClick={() => clearDeferAuto.mutate()}
            >
              {t('toolbox.sysupdate.autoDeferClear')}
            </Button>
          )}
        </div>
        <p className='text-[11px] text-gray-500'>{autoStatusText}</p>
      </div>

      {/* Apt zrcadla / zdroje — Debian + volitelný ACM custom */}
      <div className='border-t border-gray-700/50 pt-2 flex flex-col gap-2'>
        <button
          type='button'
          className='flex items-center gap-2 text-xs text-gray-400 hover:text-gray-200 text-left'
          onClick={() => setShowSources((v) => !v)}
        >
          <HardDrive size={14} />
          {t('toolbox.sysupdate.packageSources')}
          <span className='text-gray-600'>{showSources ? '▾' : '▸'}</span>
          {curMirror?.url && (
            <span
              className='font-mono text-[10px] text-gray-500 truncate max-w-[14rem]'
              title={curMirror.url}
            >
              {curMirror.url.replace(/^https?:\/\//, '').replace(/\/$/, '')}
            </span>
          )}
        </button>
        {showSources && (
          <div className='rounded border border-gray-700/40 p-2 flex flex-col gap-2 bg-black/20'>
            {(aptSrc.data?.keyring_issues?.length ?? 0) > 0 && (
              <Alert variant='warning'>
                <div className='text-xs'>
                  Keyring: {(aptSrc.data?.keyring_issues ?? []).join('; ')}
                </div>
              </Alert>
            )}
            <div className='text-xs text-gray-400'>
              {t('toolbox.sysupdate.debianMirror', { suite: aptSrc.data?.suite ?? '—' })}
            </div>
            <div className='flex flex-wrap items-center gap-2'>
              <select
                className='bg-black/40 border border-gray-700 rounded px-2 py-1 text-xs text-gray-200 max-w-full'
                value={mirrorId}
                onChange={(e) => setMirrorId(e.target.value)}
              >
                {presets.map((p) => <option key={p.id} value={p.id}>{p.label}</option>)}
                <option value='custom'>{t('toolbox.sysupdate.customUrl')}</option>
              </select>
              {(mirrorId === 'custom' || curMirror?.id === 'custom') && (
                <input
                  className='flex-1 min-w-[12rem] bg-black/40 border border-gray-700 rounded px-2 py-1 text-xs font-mono text-gray-200'
                  placeholder='http://mirror.example/debian/'
                  value={customMirrorUrl}
                  onChange={(e) => setCustomMirrorUrl(e.target.value)}
                />
              )}
              <Button
                size='sm'
                loading={setMirror.isPending}
                disabled={busy || (mirrorId === 'custom' && !customMirrorUrl.trim())}
                onClick={() => setMirror.mutate()}
              >
                {t('toolbox.sysupdate.useMirror')}
              </Button>
            </div>

            <div className='text-xs text-gray-400 mt-1'>{t('toolbox.sysupdate.otherSources')}</div>
            <div className='max-h-28 overflow-auto divide-y divide-gray-800/60'>
              {thirdParty.length === 0 && (
                <div className='text-xs text-gray-600 px-1 py-1'>
                  {t('toolbox.sysupdate.noThirdParty')}
                </div>
              )}
              {thirdParty.map((src) => (
                <div
                  key={src.path}
                  className='flex items-center justify-between gap-2 px-1 py-1 text-xs'
                >
                  <span className='font-mono truncate text-gray-300' title={src.lines.join(' | ')}>
                    {src.name}
                    {src.kind === 'acm-custom' && (
                      <Badge variant='blue' className='ml-1'>ACM</Badge>
                    )}
                  </span>
                  <Button
                    size='sm'
                    variant='ghost'
                    disabled={busy}
                    loading={toggleSource.isPending &&
                      toggleSource.variables?.source_name === src.name}
                    onClick={() =>
                      toggleSource.mutate({
                        source_name: src.name,
                        enabled: !src.enabled,
                      })}
                  >
                    {src.enabled ? t('toolbox.sysupdate.disable') : t('toolbox.sysupdate.enable')}
                  </Button>
                </div>
              ))}
            </div>

            <div className='text-xs text-gray-400 mt-1'>
              {t('toolbox.sysupdate.acmRepoHint')}
            </div>
            <div className='flex flex-wrap items-center gap-2'>
              <input
                className='flex-1 min-w-[12rem] bg-black/40 border border-gray-700 rounded px-2 py-1 text-xs font-mono text-gray-200'
                placeholder='deb [arch=amd64] https://apt.example/acm trixie main'
                value={acmRepoLine}
                onChange={(e) => setAcmRepoLine(e.target.value)}
              />
              <Button
                size='sm'
                loading={setAcmRepo.isPending}
                disabled={busy || !acmRepoLine.trim()}
                onClick={() => setAcmRepo.mutate()}
              >
                {t('toolbox.sysupdate.saveAcmRepo')}
              </Button>
              {hasAcmCustom && (
                <Button
                  size='sm'
                  variant='ghost'
                  loading={removeAcmRepo.isPending}
                  disabled={busy}
                  onClick={() => removeAcmRepo.mutate()}
                >
                  {t('toolbox.sysupdate.remove')}
                </Button>
              )}
            </div>
            <p className='text-[10px] text-gray-600 leading-relaxed'>
              {t('toolbox.sysupdate.sourcesNote')}
            </p>
          </div>
        )}
      </div>

      {/* Seznam: upgrady + NOVÉ balíčky + autoremove kandidáti */}
      {!running &&
        ((os.data?.packages?.length ?? 0) > 0 || newPkgs.length > 0 || autoPkgs.length > 0) && (
        <div className='max-h-48 overflow-auto rounded-xl border border-gray-700/40 divide-y divide-gray-800/60'>
          {(os.data?.packages?.length ?? 0) > 0 && (
            <div className='px-2 py-1 text-[10px] uppercase tracking-wide text-gray-500 bg-gray-900/40'>
              {t('toolbox.sysupdate.toUpgrade', { count: os.data?.packages?.length })}
            </div>
          )}
          {(os.data?.packages ?? []).map((p) => (
            <div key={p.name} className='flex items-center justify-between gap-2 px-2 py-1 text-xs'>
              <span className='flex items-center gap-1.5 min-w-0'>
                {p.category === 'kernel' && <Badge variant='error'>kernel</Badge>}
                {p.category === 'security' && <Badge variant='warning'>security</Badge>}
                {p.excluded_by && (
                  <span title={t('toolbox.sysupdate.excludedByTitle', { pattern: p.excluded_by })}>
                    <Badge variant='info'>{t('toolbox.sysupdate.excludedBadge')}</Badge>
                  </span>
                )}
                <span className='font-mono truncate text-gray-300'>{p.name}</span>
              </span>
              <span className='flex items-center gap-1 shrink-0'>
                {(p.current || p.available) && (
                  <span className='font-mono text-gray-500'>
                    {p.current} → {p.available}
                  </span>
                )}
                {osAutoOn && !p.excluded_by && (
                  <Button
                    size='icon'
                    variant='ghost'
                    disabled={saveAuto.isPending}
                    title={t('toolbox.sysupdate.excludeThis', { name: p.name })}
                    aria-label={t('toolbox.sysupdate.excludeThis', { name: p.name })}
                    onClick={() => addExclude(p.name)}
                  >
                    <BellOff size={12} />
                  </Button>
                )}
              </span>
            </div>
          ))}
          {newPkgs.length > 0 && (
            <>
              <div className='px-2 py-1 text-[10px] uppercase tracking-wide text-blue-400/90 bg-blue-500/5'>
                {t('toolbox.sysupdate.willInstallNew', { count: newPkgs.length })}
              </div>
              {newPkgs.map((p) => (
                <div
                  key={`new-${p.name}`}
                  className='flex items-center justify-between gap-2 px-2 py-1 text-xs'
                >
                  <span className='flex items-center gap-1.5 min-w-0'>
                    <Badge variant='info'>new</Badge>
                    <span className='font-mono truncate text-gray-300'>{p.name}</span>
                  </span>
                  {p.version && (
                    <span className='font-mono text-gray-500 shrink-0'>{p.version}</span>
                  )}
                </div>
              ))}
            </>
          )}
          {autoPkgs.length > 0 && (
            <>
              <div className='px-2 py-1 text-[10px] uppercase tracking-wide text-amber-400/90 bg-amber-500/5'>
                {t('toolbox.sysupdate.noLongerNeeded', { count: autoPkgs.length })}
              </div>
              {autoPkgs.map((p) => (
                <div
                  key={`ar-${p.name}`}
                  className='flex items-center justify-between gap-2 px-2 py-1 text-xs'
                >
                  <span className='flex items-center gap-1.5 min-w-0'>
                    <Badge variant='warning'>auto</Badge>
                    <span className='font-mono truncate text-gray-300'>{p.name}</span>
                  </span>
                  {p.version && (
                    <span className='font-mono text-gray-500 shrink-0'>{p.version}</span>
                  )}
                </div>
              ))}
            </>
          )}
        </div>
      )}

      {/* Živý log — jen když něco běží nebo je log */}
      {(running || (activeLog && activeLog.length > 0)) && (
        <div className='border-t border-gray-700/50 pt-2'>
          <div className='flex items-center gap-2 mb-1 text-xs text-gray-400'>
            {running
              ? (
                <>
                  <RotateCw size={12} className='animate-spin' />
                  {t('toolbox.sysupdate.runningUpdate', { channel: acmRun ? 'ACM' : 'OS' })}
                </>
              )
              : <>{t('toolbox.sysupdate.lastRun')}</>}
          </div>
          <pre className='acm-terminal p-2 text-[11px] leading-snug max-h-56 overflow-auto whitespace-pre-wrap'>
            {activeLog || '—'}
          </pre>
        </div>
      )}

      {/* Upozornění na potřebu restartu/rebootu */}
      {needsRestart && !running && (
        <Alert variant='warning'>
          <div className='flex flex-col gap-2'>
            <div className='flex items-center gap-2 font-medium'>
              <AlertTriangle size={14} />
              {rebootReq
                ? t('toolbox.sysupdate.rebootRequired', {
                  reason: rebootReason ? ` (${rebootReason})` : '',
                })
                : t('toolbox.sysupdate.servicesAwaitRestart')}
            </div>
            {rebootReq && os.data?.running_kernel && os.data?.newest_kernel &&
              os.data.running_kernel !== os.data.newest_kernel && (
              <div className='text-xs text-gray-400'>
                {t('toolbox.sysupdate.kernelRunningVsNew', {
                  running: os.data.running_kernel,
                  newest: os.data.newest_kernel,
                })}
              </div>
            )}
            {svcRestart.length > 0 && (
              <div className='text-xs text-gray-400'>
                {t('toolbox.sysupdate.waitingServices', { list: svcRestart.join(', ') })}
              </div>
            )}
            {!rebootReq && svcRestart.length > 0 && !inProgress && (
              <div>
                <Button
                  size='sm'
                  loading={restartSvc.isPending}
                  onClick={() => restartSvc.mutate()}
                >
                  <RotateCw size={13} /> {t('toolbox.sysupdate.restartAcmServices')}
                </Button>
              </div>
            )}
          </div>
        </Alert>
      )}

      {/* Progress panel — reboot / restart služeb (plná obrazovka bloku, bez aktivních tlačítek) */}
      {progress && progress.phase !== 'idle' && (
        <div className='rounded-2xl border border-amber-500/30 bg-amber-500/5 p-4 space-y-3'>
          <div className='flex items-start gap-3'>
            {progress.phase === 'online'
              ? <Activity size={18} className='text-emerald-400 shrink-0 mt-0.5' />
              : progress.phase === 'timeout'
              ? <AlertTriangle size={18} className='text-amber-400 shrink-0 mt-0.5' />
              : <RotateCw size={18} className='text-amber-400 animate-spin shrink-0 mt-0.5' />}
            <div className='min-w-0 flex-1 space-y-1'>
              <div className='text-sm font-semibold text-gray-100'>
                {progress.kind === 'reboot'
                  ? t('toolbox.sysupdate.progressRebootTitle', 'Reboot: {{node}}', {
                    node: nodeLabel,
                  })
                  : t('toolbox.sysupdate.progressServicesTitle', 'Restart služeb: {{node}}', {
                    node: nodeLabel,
                  })}
              </div>
              <div className='text-xs text-gray-400'>
                {progress.phase === 'sending' &&
                  t('toolbox.sysupdate.phaseSending', 'Odesílám příkaz…')}
                {progress.phase === 'waiting_down' &&
                  t(
                    'toolbox.sysupdate.phaseWaitingDown',
                    'Čekám, až stroj spadne / přestane odpovídat…',
                  )}
                {progress.phase === 'waiting_up' &&
                  t(
                    'toolbox.sysupdate.phaseWaitingUp',
                    'Čekám na náběh služeb a API…',
                  )}
                {progress.phase === 'online' &&
                  t('toolbox.sysupdate.phaseOnline', 'Node je zpět online.')}
                {progress.phase === 'timeout' &&
                  t(
                    'toolbox.sysupdate.phaseTimeout',
                    'Čas vypršel — zkontroluj node ručně.',
                  )}
              </div>
              <div className='text-[11px] text-gray-500 tabular-nums'>
                {t('toolbox.sysupdate.elapsed', 'Uplynulo: {{t}}', {
                  t: fmtUptime(
                    Math.floor((Date.now() - progress.startedAt) / 1000),
                    t as never,
                  ),
                })}
                {host?.uptime_seconds != null && progress.phase === 'online' && (
                  <>
                    {' · '}
                    {t('toolbox.sysupdate.uptimeAfter', 'uptime po bootu: {{t}}', {
                      t: fmtUptime(host.uptime_seconds, t as never),
                    })}
                  </>
                )}
              </div>
              {Object.keys(services).length > 0 &&
                (progress.phase === 'waiting_up' || progress.phase === 'online') && (
                <div className='flex flex-wrap gap-1.5 pt-1'>
                  {Object.entries(services).map(([name, st]) => (
                    <Badge
                      key={name}
                      variant={st === 'active' ? 'success' : st === 'failed' ? 'error' : 'warning'}
                      className='text-[10px] font-mono'
                    >
                      {name.replace(/^acm-/, '')}:{st}
                    </Badge>
                  ))}
                </div>
              )}
            </div>
          </div>
          {(progress.phase === 'timeout' || progress.phase === 'online') && (
            <div className='flex justify-end'>
              <Button size='sm' variant='secondary' onClick={() => clearProgress()}>
                {t('toolbox.sysupdate.progressDismiss', 'Zavřít')}
              </Button>
            </div>
          )}
        </div>
      )}

      {/* Reboot stroje — skryto během progressu */}
      {!running && !inProgress && (
        <div className='border-t border-gray-700/50 pt-2 flex flex-col gap-2'>
          <div className='text-xs text-gray-400'>
            {t('toolbox.sysupdate.machineReboot', { node: nodeLabel })}
          </div>

          <div className='flex flex-col gap-2'>
            <div className='flex flex-wrap items-center gap-2'>
              <Button
                size='sm'
                variant={rebootReq ? 'danger' : 'secondary'}
                onClick={() => setRebootModal(true)}
              >
                <RotateCw size={13} /> {t('toolbox.sysupdate.rebootNow')}
              </Button>
              <span className='text-xs text-gray-500'>{t('toolbox.sysupdate.orSchedule')}</span>
              {[
                { label: t('toolbox.sysupdate.in15min'), when: '+15' },
                { label: t('toolbox.sysupdate.in1h'), when: '+60' },
                { label: t('toolbox.sysupdate.in3h'), when: '+180' },
              ].map((p) => (
                <Button
                  key={p.when}
                  size='sm'
                  variant='ghost'
                  loading={rebootSched.isPending && rebootSched.variables === p.when}
                  onClick={() => rebootSched.mutate(p.when)}
                >
                  {p.label}
                </Button>
              ))}
            </div>
            <div className='flex flex-wrap items-center gap-2'>
              <span className='text-xs text-gray-500'>{t('toolbox.sysupdate.atTime')}</span>
              <input
                type='time'
                value={schedWhen}
                onChange={(e) => setSchedWhen(e.target.value)}
                className='bg-black/30 border border-gray-700 rounded px-2 py-1 text-xs text-gray-200'
              />
              <Button
                size='sm'
                variant='ghost'
                disabled={!schedWhen.trim()}
                loading={rebootSched.isPending && rebootSched.variables === schedWhen.trim()}
                onClick={() => rebootSched.mutate(schedWhen.trim())}
              >
                <Calendar size={13} /> {t('toolbox.sysupdate.schedule')}
              </Button>
              {schedInfo?.scheduled && (
                <span className='text-xs text-gray-500'>
                  {t('toolbox.sysupdate.nextScheduled', {
                    when: schedInfo.at
                      ? new Date(schedInfo.at).toLocaleString()
                      : t('toolbox.sysupdate.scheduled'),
                  })}
                  <button
                    type='button'
                    onClick={() => rebootCancel.mutate()}
                    className='ml-2 text-gray-500 hover:text-red-400 underline'
                  >
                    {t('toolbox.sysupdate.cancelSchedule')}
                  </button>
                </span>
              )}
            </div>
          </div>
        </div>
      )}

      <Modal
        open={rebootModal}
        onClose={() => setRebootModal(false)}
        title={t('toolbox.sysupdate.rebootModalTitle', {
          node: nodeLabel,
        })}
        size='md'
      >
        <div className='flex flex-col gap-3'>
          <p className='text-sm text-gray-300'>
            {t('toolbox.sysupdate.rebootModalBody')}
            {!scope.node && t('toolbox.sysupdate.rebootModalLocalNote')}
          </p>
          <div className='flex justify-end gap-2'>
            <Button size='sm' variant='ghost' onClick={() => setRebootModal(false)}>
              {t('toolbox.sysupdate.cancel')}
            </Button>
            <Button
              size='sm'
              variant='danger'
              loading={rebootNow.isPending}
              onClick={() => rebootNow.mutate()}
            >
              <RotateCw size={13} /> {t('toolbox.sysupdate.reboot')}
            </Button>
          </div>
        </div>
      </Modal>

      <Modal
        open={installModal !== null}
        onClose={() => setInstallModal(null)}
        title={t(
          installModal === 'security'
            ? 'toolbox.sysupdate.installSecurityModalTitle'
            : 'toolbox.sysupdate.installAllModalTitle',
          { node: nodeLabel },
        )}
        size='md'
      >
        <div className='flex flex-col gap-3'>
          <p className='text-sm text-gray-300'>{t('toolbox.sysupdate.installNowBody')}</p>
          <p className='text-xs text-gray-400'>
            {installModal === 'security'
              ? t('toolbox.sysupdate.installNowSecurityLine', { count: secPending })
              : t('toolbox.sysupdate.installNowAllLine', { count: s.total ?? 0 })}
          </p>
          {excludedPkgs.length > 0 && (
            <Alert variant='warning'>
              {t('toolbox.sysupdate.installNowExcludedNote', {
                count: excludedPkgs.length,
                names: excludedPkgs.map((p) => p.name).join(', '),
              })}
            </Alert>
          )}
          <div className='flex flex-wrap justify-end gap-2'>
            <Button size='sm' variant='ghost' onClick={() => setInstallModal(null)}>
              {t('toolbox.sysupdate.cancel')}
            </Button>
            {installModal === 'security'
              ? (
                <Button
                  size='sm'
                  variant='primary'
                  disabled={busy || secPending === 0}
                  onClick={() => {
                    setInstallModal(null);
                    updOsSec.mutate();
                  }}
                >
                  <Shield size={13} /> {t('toolbox.sysupdate.installSecurityBtn', { count: secPending })}
                </Button>
              )
              : (
                <Button
                  size='sm'
                  variant='primary'
                  disabled={busy || (s.total ?? 0) === 0}
                  onClick={() => {
                    setInstallModal(null);
                    updOs.mutate();
                  }}
                >
                  <Download size={13} /> {t('toolbox.sysupdate.installAllBtn', { count: s.total ?? 0 })}
                </Button>
              )}
          </div>
        </div>
      </Modal>

      <Modal
        open={arModal}
        onClose={() => !osAutoremove.isPending && setArModal(false)}
        title={t('toolbox.sysupdate.autoremoveTitle', { count: autoPkgs.length })}
        size='md'
      >
        <div className='flex flex-col gap-3'>
          <p className='text-sm text-gray-400'>
            {t('toolbox.sysupdate.autoremoveBody', { node: nodeLabel })}
          </p>
          <div className='max-h-56 overflow-auto rounded-xl border border-gray-700/50 divide-y divide-gray-800/60'>
            {autoPkgs.length === 0
              ? (
                <p className='text-xs text-gray-500 p-3 text-center'>
                  {t('toolbox.sysupdate.noPackagesToClean')}
                </p>
              )
              : autoPkgs.map((p) => (
                <div
                  key={p.name}
                  className='flex items-center justify-between gap-2 px-3 py-1.5 text-xs'
                >
                  <span className='font-mono text-gray-200 truncate'>{p.name}</span>
                  {p.version && (
                    <span className='font-mono text-gray-500 shrink-0'>{p.version}</span>
                  )}
                </div>
              ))}
          </div>
          <div className='flex justify-end gap-2'>
            <Button
              size='sm'
              variant='ghost'
              disabled={osAutoremove.isPending}
              onClick={() => setArModal(false)}
            >
              {t('toolbox.sysupdate.cancel')}
            </Button>
            <Button
              size='sm'
              variant='warning'
              loading={osAutoremove.isPending}
              disabled={autoPkgs.length === 0}
              onClick={() => osAutoremove.mutate()}
            >
              {t('toolbox.sysupdate.removePackages', { count: autoPkgs.length })}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Chybové hlášky akcí */}
      {[
        updAcm,
        updOs,
        updOsSec,
        restartSvc,
        rebootNow,
        rebootSched,
        rebootCancel,
        setMirror,
        toggleSource,
        setAcmRepo,
        removeAcmRepo,
        saveAuto,
        deferAuto,
        clearDeferAuto,
      ].map((m, i) => m.isError
        ? (
          <div key={i} className='text-xs text-red-400'>
            {(m.error as Error)?.message ?? t('toolbox.sysupdate.actionFailed')}
          </div>
        )
        : null
      )}
    </div>
  );
}

export const TOOLBOX_WIDGETS: WidgetSpec[] = [
  {
    id: 'toolbox.health',
    title: 'Health checks',
    module: 'toolbox',
    kind: 'custom',
    format: 'lg',
    formats: ['md', 'lg'],
    icon: Activity,
    since: '2.1.0',
    component: HealthPanel,
  },
  {
    id: 'toolbox.security',
    title: 'Security scan',
    module: 'toolbox',
    kind: 'custom',
    format: 'lg',
    formats: ['md', 'lg'],
    icon: Shield,
    since: '2.1.0',
    component: SecurityPanel,
  },
  {
    id: 'toolbox.rollout',
    title: 'Fleet rollout',
    module: 'toolbox',
    kind: 'custom',
    format: 'lg',
    formats: ['md', 'lg'],
    icon: GitCommit,
    since: '2.1.0',
    component: FleetRolloutCard,
  },
  {
    id: 'toolbox.sysupdate',
    title: 'System updates',
    module: 'toolbox',
    kind: 'custom',
    format: 'lg',
    formats: ['md', 'lg'],
    icon: Cpu,
    // Kompozitní: bez `mcp`, data si tahá komponenta sama (poll + akce, node-aware).
    component: SysUpdateWidget,
  },
  {
    id: 'toolbox.backup',
    title: 'Backup',
    module: 'toolbox',
    kind: 'custom',
    format: 'lg',
    formats: ['md', 'lg'],
    icon: Archive,
    since: '2.1.0',
    component: ToolboxBackupPanel,
  },
  {
    id: 'toolbox.cleaner',
    title: 'Cleaner',
    module: 'toolbox',
    kind: 'custom',
    format: 'lg',
    formats: ['md', 'lg'],
    icon: Trash2,
    since: '2.1.0',
    component: CleanerPanel,
  },
  {
    id: 'toolbox.watchdog',
    title: 'Watchdog',
    module: 'toolbox',
    kind: 'custom',
    format: 'lg',
    formats: ['md', 'lg'],
    icon: Eye,
    since: '2.1.0',
    component: WatchdogPanel,
  },
  {
    id: 'toolbox.crash-recovery',
    title: 'UI crash recovery',
    module: 'toolbox',
    kind: 'custom',
    format: 'lg',
    formats: ['md', 'lg'],
    icon: Shield,
    since: '2.1.0',
    component: CrashRecoveryPanel,
  },
  {
    id: 'toolbox.resources',
    title: 'Resources',
    module: 'toolbox',
    kind: 'custom',
    format: 'lg',
    formats: ['md', 'lg'],
    icon: HardDrive,
    since: '2.1.0',
    component: ResourcesPanel,
  },
  {
    id: 'toolbox.system',
    title: 'System',
    module: 'toolbox',
    kind: 'custom',
    format: 'lg',
    formats: ['md', 'lg'],
    icon: Settings,
    since: '2.1.0',
    component: SystemPanel,
  },
  {
    id: 'toolbox.acm-link',
    title: 'ACMLink',
    module: 'toolbox',
    kind: 'custom',
    format: 'lg',
    formats: ['md', 'lg'],
    icon: Puzzle,
    since: '2.1.0',
    component: AcmLinkPanel,
  },
];
